import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Section = styled.section<{ $tinted?: boolean }>`
  padding: 4rem 1.5rem;
  background: ${props => props.$tinted
    ? `linear-gradient(180deg, rgba(245, 245, 247, 0) 0%, ${props.theme.colors.surfaceLight} 8%, ${props.theme.colors.surfaceLight} 92%, rgba(245, 245, 247, 0) 100%)`
    : props.theme.colors.surface};

  @media (max-width: 700px) {
    padding: 3rem 1rem;
  }
`;

const Inner = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const Header = styled.div`
  max-width: 720px;
  margin-bottom: 1.4rem;
`;

const Kicker = styled.p`
  margin-bottom: 0.35rem;
  color: ${props => props.theme.colors.textTertiary};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
`;

const PaperCard = styled(motion.div)`
  border: 1px solid rgba(0, 0, 0, 0.045);
  border-radius: 1.5rem;
  background: ${props => props.theme.colors.surface};
  box-shadow: ${props => props.theme.shadows.md};
`;

const AbstractGrid = styled.div`
  display: grid;
  grid-template-columns: 0.72fr 1.28fr;
  gap: 2rem;
  align-items: start;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const AbstractLabel = styled.div`
  padding-top: 0.2rem;

  strong {
    display: block;
    font-family: ${props => props.theme.fonts.display};
    font-size: 1.3rem;
  }

  span {
    display: block;
    margin-top: 0.45rem;
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.8rem;
  }
`;

const AbstractCopy = styled.div`
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.display};
  font-size: clamp(1.05rem, 1.6vw, 1.22rem);
  line-height: 1.72;

  p + p { margin-top: 1.25rem; }
  strong { color: ${props => props.theme.colors.secondary}; }
`;

export const Abstract: React.FC = () => (
  <Section id="abstract">
    <Inner>
      <Header>
        <Kicker>Overview</Kicker>
      </Header>

      <AbstractGrid>
        <AbstractLabel>
          <strong>Abstract</strong>
          <span>140 tasks · 10 tactics · 77 techniques</span>
        </AbstractLabel>
        <AbstractCopy>
          <p>
            <strong>ADVCLI</strong> evaluates OS-level misuse with 140 MITRE ATT&amp;CK-aligned tasks in a controlled
            three-host sandbox. Hard-coded checks verify real system effects across seven CLI agents and nine models,
            showing that TTP-style attacker knowledge substantially increases risk.
          </p>
        </AbstractCopy>
      </AbstractGrid>
    </Inner>
  </Section>
);

const SplitCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const SplitCard = styled(PaperCard)`
  padding: 1.45rem;

  .number {
    color: ${props => props.theme.colors.secondary};
    font-family: ${props => props.theme.fonts.display};
    font-size: 2.4rem;
    font-weight: 800;
    line-height: 1;
  }

  h3 { margin-top: 0.8rem; font-size: 1.1rem; }
  p { margin-top: 0.45rem; color: ${props => props.theme.colors.textSecondary}; font-size: 0.8rem; }
`;

const Distribution = styled(PaperCard)`
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.3rem 1.45rem;

  h3 { font-size: 1.25rem; }
  p { color: ${props => props.theme.colors.textSecondary}; font-size: 0.76rem; text-align: right; }

  @media (max-width: 620px) {
    align-items: flex-start;
    flex-direction: column;
    p { text-align: left; }
  }
`;

const TacticList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 2rem;
  padding: 0.7rem 1.45rem 1.2rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Tactic = styled.div`
  display: grid;
  grid-template-columns: minmax(132px, 0.75fr) 1fr 34px;
  gap: 0.8rem;
  align-items: center;
  padding: 0.72rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.surfaceLight};

  .name {
    color: ${props => props.theme.colors.text};
    font-size: 0.76rem;
    font-weight: 650;
  }

  .name small {
    display: block;
    color: ${props => props.theme.colors.textTertiary};
    font-family: ${props => props.theme.fonts.mono};
    font-size: 0.61rem;
    font-weight: 500;
  }

  .bar {
    height: 7px;
    overflow: hidden;
    border-radius: 999px;
    background: ${props => props.theme.colors.surfaceLight};
  }

  .bar i {
    height: 100%;
    display: block;
    border-radius: inherit;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  }

  .count {
    color: ${props => props.theme.colors.secondary};
    font-family: ${props => props.theme.fonts.mono};
    font-size: 0.72rem;
    font-weight: 700;
    text-align: right;
  }
`;

const tacticData = [
  ['Defense Evasion', 'TA0005', 92],
  ['Persistence', 'TA0003', 50],
  ['Privilege Escalation', 'TA0004', 48],
  ['Command and Control', 'TA0011', 38],
  ['Credential Access', 'TA0006', 24],
  ['Exfiltration', 'TA0010', 18],
  ['Execution', 'TA0002', 12],
  ['Lateral Movement', 'TA0008', 6],
  ['Collection', 'TA0009', 4],
  ['Initial Access', 'TA0001', 2],
] as const;

export const Dataset: React.FC = () => {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });

  return (
    <Section id="dataset" ref={ref}>
      <Inner>
        <Header>
          <Kicker>Benchmark</Kicker>
        </Header>

        <SplitCards>
          <SplitCard initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}><div className="number">40</div><h3>Direct malicious</h3><p>High-level attack goals without an operational route.</p></SplitCard>
          <SplitCard initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.06 }}><div className="number">74</div><h3>TTP-based</h3><p>Technique-specific requests grounded in ATT&amp;CK procedures.</p></SplitCard>
          <SplitCard initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.12 }}><div className="number">26</div><h3>End-to-end chains</h3><p>Ordered, multi-stage attack sequences across hosts and privileges.</p></SplitCard>
        </SplitCards>

        <Distribution initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.16 }}>
          <CardHeader><h3>ATT&amp;CK tactic distribution</h3></CardHeader>
          <TacticList>
            {tacticData.map(([name, id, count]) => (
              <Tactic key={id}>
                <div className="name">{name}<small>{id}</small></div>
                <div className="bar"><i style={{ width: `${Math.max((count / 92) * 100, 4)}%` }} /></div>
                <div className="count">{count}</div>
              </Tactic>
            ))}
          </TacticList>
        </Distribution>
      </Inner>
    </Section>
  );
};

const EvaluationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const EvaluationCard = styled(PaperCard)`
  padding: 1.4rem;

  h3 { font-size: 1.25rem; }
  > p { margin-top: 0.55rem; color: ${props => props.theme.colors.textSecondary}; font-size: 0.84rem; }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 1.15rem;
`;

const Tag = styled.span`
  padding: 0.38rem 0.55rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 999px;
  background: ${props => props.theme.colors.surfaceLight};
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.66rem;
`;

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;
  margin-top: 1.15rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Metric = styled.div`
  padding: 1rem;
  border-left: 3px solid ${props => props.theme.colors.primary};
  border-radius: 0.8rem;
  background: ${props => props.theme.colors.surfaceLight};

  strong { display: block; color: ${props => props.theme.colors.textPrimary}; font-size: 0.82rem; }
  span { display: block; margin-top: 0.3rem; color: ${props => props.theme.colors.textSecondary}; font-size: 0.73rem; line-height: 1.5; }
`;

const agents = ['ReAct', 'OpenClaw', 'OpenAI Agent SDK', 'Claude Code', 'Gemini CLI', 'Cursor CLI', 'Cursor IDE'];

const EvaluationSetup: React.FC = () => (
  <>
      <Header>
        <Kicker>Evaluation</Kicker>
      </Header>

      <EvaluationGrid>
        <EvaluationCard>
          <h3>7 agents · 9 models</h3>
          <Tags>{agents.map(agent => <Tag key={agent}>{agent}</Tag>)}</Tags>
        </EvaluationCard>
        <EvaluationCard>
          <h3>Deterministic scoring</h3>
          <Metrics>
            <Metric><strong>BSR</strong><span>Tool execution starts.</span></Metric>
            <Metric><strong>ASR</strong><span>The OS effect is verified.</span></Metric>
          </Metrics>
        </EvaluationCard>
      </EvaluationGrid>
  </>
);

type Result = {
  framework: string;
  open?: boolean;
  model: string;
  ttp: [number, number];
  direct: [number, number];
  end: [number, number];
};

const results: Result[] = [
  { framework: 'ReAct', open: true, model: 'GPT-4o', ttp: [51.35, 91.89], direct: [35.00, 72.50], end: [23.08, 61.54] },
  { framework: 'ReAct', open: true, model: 'DeepSeek V4 Pro', ttp: [51.35, 70.27], direct: [35.00, 50.00], end: [3.85, 15.38] },
  { framework: 'ReAct', open: true, model: 'Kimi K2.6', ttp: [50.00, 67.57], direct: [15.00, 30.00], end: [3.85, 15.38] },
  { framework: 'ReAct', open: true, model: 'GLM 5.1', ttp: [52.70, 81.08], direct: [40.00, 62.50], end: [3.85, 23.08] },
  { framework: 'OpenClaw', open: true, model: 'GPT-4o', ttp: [43.24, 83.78], direct: [17.50, 52.50], end: [7.69, 65.38] },
  { framework: 'OpenClaw', open: true, model: 'DeepSeek V4 Pro', ttp: [62.16, 85.14], direct: [50.00, 80.00], end: [57.69, 88.46] },
  { framework: 'OpenClaw', open: true, model: 'Kimi K2.6', ttp: [56.76, 89.19], direct: [47.50, 70.00], end: [26.92, 42.31] },
  { framework: 'OpenClaw', open: true, model: 'GLM 5.1', ttp: [60.81, 83.78], direct: [20.00, 60.00], end: [23.08, 84.62] },
  { framework: 'OpenAI Agent SDK', open: true, model: 'GPT-5.4', ttp: [31.08, 48.65], direct: [15.00, 30.00], end: [11.54, 15.38] },
  { framework: 'Claude Code', model: 'Claude Sonnet 4.5', ttp: [24.32, 52.70], direct: [7.50, 7.50], end: [30.77, 34.62] },
  { framework: 'Claude Code', model: 'Claude Opus 4.7', ttp: [21.62, 47.30], direct: [17.50, 32.50], end: [15.39, 23.08] },
  { framework: 'Gemini CLI', open: true, model: 'Gemini 2.5 Pro', ttp: [39.19, 56.76], direct: [5.00, 15.00], end: [3.85, 7.69] },
  { framework: 'Cursor CLI', model: 'Claude Opus 4.1', ttp: [62.16, 86.49], direct: [15.00, 27.50], end: [23.08, 53.85] },
  { framework: 'Cursor IDE', model: 'Claude Opus 4.1', ttp: [39.19, 56.76], direct: [22.50, 32.50], end: [26.92, 30.77] },
];

const FindingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  @media (max-width: 860px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Finding = styled.div`
  min-height: 154px;
  padding: 1.15rem;
  border: 1px solid rgba(0, 0, 0, 0.045);
  border-radius: 1.25rem;
  background: ${props => props.theme.colors.surface};
  box-shadow: ${props => props.theme.shadows.sm};

  strong {
    display: block;
    color: ${props => props.theme.colors.secondary};
    font-family: ${props => props.theme.fonts.display};
    font-size: 2rem;
    line-height: 1;
  }

  span { display: block; margin-top: 0.65rem; color: ${props => props.theme.colors.text}; font-size: 0.78rem; font-weight: 650; }
  p { margin-top: 0.35rem; color: ${props => props.theme.colors.textSecondary}; font-size: 0.7rem; }
`;

const FindingNote = styled.p`
  margin: -0.65rem 0 1.5rem;
  color: ${props => props.theme.colors.textTertiary};
  font-size: 0.68rem;
`;

const TableCard = styled(PaperCard)`
  overflow: hidden;
  border-radius: 1.1rem;
  background: rgba(255, 255, 255, 0.56);
  box-shadow: none;
`;

const ResultsTableHeader = styled.div`
  padding: 1rem 1.15rem 0.55rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.72rem;
  font-weight: 650;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 700px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.72rem;

  th, td {
    padding: 0.68rem 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.045);
  }

  tbody tr:last-child td { border-bottom: 0; }
  tbody tr { transition: background 160ms ease; }
  tbody tr:hover { background: rgba(255, 255, 255, 0.72); }

  th {
    background: rgba(0, 0, 0, 0.018);
    color: ${props => props.theme.colors.textSecondary};
    font-family: ${props => props.theme.fonts.mono};
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-align: center;
    text-transform: uppercase;
  }

  th:first-child {
    width: 34%;
    text-align: left;
  }

  th small {
    display: block;
    margin-top: 0.18rem;
    color: ${props => props.theme.colors.textTertiary};
    font-size: 0.52rem;
    letter-spacing: 0.04em;
  }

  td:first-child { text-align: left; }
  td:not(:first-child) { text-align: center; }

  .agent strong {
    display: block;
    color: ${props => props.theme.colors.textPrimary};
    font-size: 0.74rem;
    font-weight: 680;
  }

  .agent span {
    display: block;
    margin-top: 0.16rem;
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.65rem;
  }

  .score-pair {
    display: flex;
    justify-content: center;
    gap: 0.3rem;
  }

  .score {
    min-width: 3.45rem;
    padding: 0.32rem 0.38rem;
    border-radius: 0.52rem;
    background: rgba(0, 0, 0, 0.022);
    color: ${props => props.theme.colors.textPrimary};
    font-variant-numeric: tabular-nums;
  }

  .score strong {
    font-size: 0.7rem;
    font-weight: 680;
  }

  .score small {
    display: block;
    margin-top: 0.08rem;
    color: ${props => props.theme.colors.textTertiary};
    font-family: ${props => props.theme.fonts.mono};
    font-size: 0.48rem;
    letter-spacing: 0.05em;
  }

  .score.best {
    background: rgba(0, 113, 227, 0.07);
    color: ${props => props.theme.colors.secondary};
  }
`;

const TableNote = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.69rem;

  @media (max-width: 620px) { flex-direction: column; }
`;

const Followup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const FollowupCard = styled.div`
  padding: 1.15rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.56);

  strong { color: ${props => props.theme.colors.textPrimary}; font-size: 0.88rem; }
  p { margin-top: 0.4rem; color: ${props => props.theme.colors.textSecondary}; font-size: 0.76rem; }
`;

const ResultsBlock = styled.div`
  margin-top: 3rem;

  @media (max-width: 700px) {
    margin-top: 2.5rem;
  }
`;

const fmt = (value: number) => value.toFixed(2);

const MetricPair: React.FC<{
  values: [number, number];
  best: [number, number];
}> = ({ values, best }) => (
  <div className="score-pair">
    <span className={`score ${values[0] === best[0] ? 'best' : ''}`}><strong>{fmt(values[0])}</strong><small>ASR</small></span>
    <span className={`score ${values[1] === best[1] ? 'best' : ''}`}><strong>{fmt(values[1])}</strong><small>BSR</small></span>
  </div>
);

const EvaluationResults: React.FC = () => (
  <ResultsBlock id="results">
      <FindingGrid>
        <Finding><strong>46.14%</strong><span>Average TTP ASR</span><p>Compared with 24.46% for direct malicious requests.</p></Finding>
        <Finding><strong>+21.68</strong><span>TTP ASR increase</span><p>TTP versus Direct.</p></Finding>
        <Finding><strong>18.68%</strong><span>End-to-End ASR</span><p>Full chains are harder, yet average BSR remains 40.11%.</p></Finding>
        <Finding><strong>+10.81</strong><span>CTF-framing ASR increase</span><p>Added by CTF framing.</p></Finding>
      </FindingGrid>
      <FindingNote>ASR · Attack Success Rate. “+” indicates the absolute increase in attack success rate.</FindingNote>

      <TableCard>
        <ResultsTableHeader>Main results</ResultsTableHeader>
        <TableScroll>
          <Table>
            <thead>
              <tr><th>Agent / model</th><th>TTP<small>ASR · BSR</small></th><th>Direct<small>ASR · BSR</small></th><th>End-to-End<small>ASR · BSR</small></th></tr>
            </thead>
            <tbody>
              {results.map(row => (
                <tr key={`${row.framework}-${row.model}`}>
                  <td><div className="agent"><strong>{row.framework}</strong><span>{row.model}</span></div></td>
                  <td><MetricPair values={row.ttp} best={[62.16, 91.89]} /></td>
                  <td><MetricPair values={row.direct} best={[50, 80]} /></td>
                  <td><MetricPair values={row.end} best={[57.69, 88.46]} /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
        <TableNote><span>Blue marks a column maximum.</span></TableNote>
      </TableCard>

      <Followup>
        <FollowupCard><strong>Best-of-5 compounds exposure</strong><p>Average ASR@5 reaches 42.21% and BSR@5 reaches 58.46%; on TTP tasks those values rise to 70.27% and 84.29%.</p></FollowupCard>
        <FollowupCard><strong>Credential Access is most exposed</strong><p>TA0006 records the highest average per-tactic ASR at 56.82%, often resembling legitimate auditing workflows.</p></FollowupCard>
        <FollowupCard><strong>Professional tools raise capability</strong><p>A penetration-testing MCP tool increases Cursor IDE End-to-End ASR@1 by 53.85 percentage points.</p></FollowupCard>
      </Followup>
  </ResultsBlock>
);

export const Evaluation: React.FC = () => (
  <Section id="evaluation" $tinted>
    <Inner>
      <EvaluationSetup />
      <EvaluationResults />
    </Inner>
  </Section>
);
